import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, PiggyBank, TrendingDown, CheckCircle2, CalendarDays } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { useCurrency } from '@/hooks/useCurrency';
import {
  fetchBudgets,
  createBudget,
  updateBudget,
  deleteBudget,
} from '@/features/budgets/budgetsSlice';
import type { BudgetApiPayload } from '@/features/budgets/budgetsSlice';
import { fetchCategories } from '@/features/categories/categoriesSlice';
import BudgetCard from '@/components/budgets/BudgetCard';
import BudgetForm from '@/components/budgets/BudgetForm';
import type { BudgetFormData } from '@/components/budgets/BudgetForm';
import PageGuide from '@/components/common/PageGuide';
import { budgetsGuide } from '@/components/common/guideContent';
import type { Budget } from '@/lib/types';
import { useFABContext } from '@/components/common/FABContext';

export default function Budgets() {
  useDocumentTitle('Budgets');
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const { fmt } = useCurrency();
  const { budgets, isLoading } = useAppSelector((state) => state.budgets);
  const categories = useAppSelector((state) => state.categories.categories);

  const [showForm, setShowForm] = useState(false);
  const [editingBudget, setEditingBudget] = useState<Budget | null>(null);

  // [T-FAB] Register quick-add action so the global FAB can open this form
  const { registerFABActions } = useFABContext();
  useEffect(() => {
    registerFABActions({ onAddBudget: () => { setEditingBudget(null); setShowForm(true); } });
    return () => registerFABActions({});
  }, [registerFABActions]);

  // [T-FAB-DRAFT] Auto-open add form when navigating via a draft link from FAB
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if ((location.state as { openForm?: boolean } | null)?.openForm) {
      setEditingBudget(null);
      setShowForm(true);
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  // [T71P1] Month/Year filter for multi-budget support
  const now = new Date();
  const [filterYear, setFilterYear] = useState<number | undefined>(now.getFullYear());
  const [filterMonth, setFilterMonth] = useState<number | undefined>(now.getMonth() + 1);

  const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  useEffect(() => {
    dispatch(fetchBudgets({ year: filterYear, month: filterMonth }));
  }, [dispatch, filterYear, filterMonth]);

  // Ensure categories are loaded so BudgetForm can show them
  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  const handleCreate = async (data: BudgetFormData) => {
    setFormLoading(true);
    try {
      await dispatch(createBudget(data as BudgetApiPayload)).unwrap();
      toast({ title: 'Budget created', description: 'Your budget has been saved.' });
      setShowForm(false);
      dispatch(fetchBudgets({ year: filterYear, month: filterMonth }));
    } catch (err: unknown) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdate = async (data: BudgetFormData) => {
    if (!editingBudget) return;
    setFormLoading(true);
    try {
      await dispatch(updateBudget({ id: editingBudget.id, data: data as BudgetApiPayload })).unwrap();
      toast({ title: 'Budget updated' });
      setEditingBudget(null);
      dispatch(fetchBudgets({ year: filterYear, month: filterMonth }));
    } catch (err: unknown) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await dispatch(deleteBudget(deletingId)).unwrap();
      toast({ title: 'Budget deleted' });
      setDeletingId(null);
    } catch (err: unknown) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    }
  };

  // Summary stats
  const totalBudgeted = budgets.reduce(
    (sum, b) => sum + b.categories.reduce((s, c) => s + c.budgeted, 0),
    0
  );
  const totalSpent = budgets.reduce(
    (sum, b) => sum + b.categories.reduce((s, c) => s + c.spent, 0),
    0
  );
  const overBudgetCount = budgets.filter((b) => {
    const budgeted = b.categories.reduce((s, c) => s + c.budgeted, 0);
    const spent = b.categories.reduce((s, c) => s + c.spent, 0);
    return spent > budgeted;
  }).length;
  const onTrackCount = budgets.length - overBudgetCount;
  const overallPct = totalBudgeted > 0 ? Math.round((totalSpent / totalBudgeted) * 100) : 0;

  return (
    <div className="space-y-4 md:space-y-8">
      <PageGuide pageKey="budgets" content={budgetsGuide} />
      {/* Header */}
      <div className="flex flex-col gap-3 md:gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Budgets</h2>
          <p className="text-sm md:text-base text-muted-foreground">Plan and track your spending</p>
        </div>
        <Button size="sm" onClick={() => setShowForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Budget
        </Button>
      </div>

      {/* [T71P1] Month/Year Filter Bar */}
      <div className="flex flex-wrap items-center gap-2">
        <CalendarDays className="h-4 w-4 text-muted-foreground" />
        <Select
          value={filterMonth !== undefined ? String(filterMonth) : 'all'}
          onValueChange={(v) => setFilterMonth(v === 'all' ? undefined : Number(v))}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="All months" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Months</SelectItem>
            {MONTHS.map((name, i) => (
              <SelectItem key={i} value={String(i + 1)}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filterYear !== undefined ? String(filterYear) : 'all'}
          onValueChange={(v) => setFilterYear(v === 'all' ? undefined : Number(v))}
        >
          <SelectTrigger className="w-[110px]">
            <SelectValue placeholder="All years" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            {Array.from({ length: 7 }, (_, i) => now.getFullYear() - 3 + i).map((yr) => (
              <SelectItem key={yr} value={String(yr)}>
                {yr}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary cards */}
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
        <Card className="glass-panel transition-all duration-200 hover:shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Budgeted</CardTitle>
            <div className="p-2 bg-primary/10 rounded-full">
              <PiggyBank className="h-4 w-4 text-primary" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold truncate">
              {fmt(totalBudgeted)}
            </div>
            <Progress value={Math.min(overallPct, 100)} className="mt-2 h-2" />
            <p className="text-xs text-muted-foreground mt-1 truncate">
              {fmt(totalSpent)} spent ({overallPct}%)
            </p>
          </CardContent>
        </Card>

        <Card className="glass-panel transition-all duration-200 hover:shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">On Track</CardTitle>
            <div className="p-2 bg-green-500/10 rounded-full">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{onTrackCount}</div>
            <p className="text-xs text-muted-foreground mt-1">budgets within limits</p>
          </CardContent>
        </Card>

        <Card className="glass-panel transition-all duration-200 hover:shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Over Budget</CardTitle>
            <div className="p-2 bg-red-500/10 rounded-full">
              <TrendingDown className="h-4 w-4 text-red-500" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-destructive">{overBudgetCount}</div>
            <p className="text-xs text-muted-foreground mt-1">budgets exceeded</p>
          </CardContent>
        </Card>
      </div>

      {/* Budget Cards Grid */}
      {isLoading && budgets.length === 0 ? (
        <div className="grid gap-3 md:gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      ) : budgets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <PiggyBank className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold">No budgets yet</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">
              Create a budget to start tracking your spending by category.
            </p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Budget
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:gap-4 md:grid-cols-2">
          {budgets.map((budget) => (
            <BudgetCard
              key={budget.id}
              budget={budget}
              onEdit={(b) => setEditingBudget(b)}
              onDelete={(id) => setDeletingId(id)}
            />
          ))}
        </div>
      )}

      {/* Create Form */}
      <BudgetForm
        open={showForm}
        onOpenChange={setShowForm}
        onSubmit={handleCreate}
        isLoading={formLoading}
        categories={categories}
      />

      {/* Edit Form */}
      <BudgetForm
        open={!!editingBudget}
        onOpenChange={(open) => !open && setEditingBudget(null)}
        onSubmit={handleUpdate}
        budget={editingBudget}
        isLoading={formLoading}
        categories={categories}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Budget</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this budget? All category tracking data for this
              budget period will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
